import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'
import { get, set, del } from 'idb-keyval'

const IDB_KEY = 'treasurelog-query-cache'
const MAX_CACHE_BYTES = 10 * 1024 * 1024 // 10MB

export function createIDBPersister(): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(IDB_KEY, trimToSize(client))
    },
    restoreClient: async () => {
      return await get<PersistedClient>(IDB_KEY)
    },
    removeClient: async () => {
      await del(IDB_KEY)
    },
  }
}

function trimToSize(client: PersistedClient): PersistedClient {
  const serialized = JSON.stringify(client)
  if (serialized.length <= MAX_CACHE_BYTES) return client

  // Sort queries by freshness (most recent first), evict oldest
  const sorted = [...client.clientState.queries].sort(
    (a, b) => (b.state.dataUpdatedAt ?? 0) - (a.state.dataUpdatedAt ?? 0),
  )

  // Estimate how many to keep based on current size ratio
  const ratio = MAX_CACHE_BYTES / serialized.length
  const keepCount = Math.max(1, Math.floor(sorted.length * ratio * 0.9))

  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: sorted.slice(0, keepCount),
    },
  }
}
